/**
 * Human wallet names (ALL-5795). Founder feedback 2026-10-02: "the wallet names ... generated look
 * pretty bad in the ux".
 *
 * A wallet's name is its `accountAlias`. People choose that alias when they create a wallet in the
 * console, but some wallets get a machine-made one:
 *   - wallets registered by the signer phone are named `trio_<32 hex>` by WalletKit
 *     (TrioWalletRegistrationService), which drops the label the phone sent;
 *   - lab and tooling wallets carry a timestamp suffix (`IncomingFunds_1790686047753`);
 *   - a wallet with no alias at all falls back to its id (`wlt_9e90…`).
 *
 * A wallet's own display name (GatewayWalletResponse.displayName, the label the signer phone sent
 * at registration, context ALL-5808) is preferred by every caller and shown as typed; it is absent
 * on older wallets and on gateways before that change.
 *
 * This is presentation only. It never changes the stored alias, and the alias, id and address stay
 * available beside the name (the wallet pages show the address with the identifier component). A
 * name a person chose is always shown as typed.
 */

export interface WalletNameInput {
  alias?: string | null;
  walletId?: string | null;
  network?: string | null;
  chain?: string | null;
  address?: string | null;
}

export interface WalletDisplayName {
  /** What to show as the wallet's name. */
  name: string;
  /** True when `name` was derived because the stored alias is machine-generated or missing. */
  derived: boolean;
  /** The stored alias, unchanged, for a title or a secondary line. Empty when there is none. */
  alias: string;
}

const HEX_TOKEN = /^[0-9a-f]{12,}$/i;
const TIMESTAMP_SUFFIX = /[_-]\d{10,}$/;
const PREFIXED_HEX = /^[a-z]{2,10}_[0-9a-f]{12,}$/i;

/** True for an alias no person would have chosen: a hash, a hex id, or the wallet id itself. */
export function isMachineAlias(alias: string, walletId?: string | null): boolean {
  const a = alias.trim();
  if (!a) return true;
  if (walletId && a === walletId.trim()) return true;
  if (HEX_TOKEN.test(a) || /^0x[0-9a-f]+$/i.test(a)) return true;
  if (PREFIXED_HEX.test(a)) return true;
  return TIMESTAMP_SUFFIX.test(a);
}

/**
 * The fields of a mapped `Wallet` (types/index.ts) that decide whether its name is machine-made.
 * Only these three are needed: the resolved name, the stored alias beside it, and the id.
 */
export interface WalletNameSource {
  name?: string | null;
  /** `Wallet.storedAlias`: set only when the shown name had to be derived from a stored alias. */
  storedAlias?: string | null;
  walletId?: string | null;
}

/**
 * True when a wallet's shown name came from neither a human alias nor a human display name, so
 * the name on screen was derived (ALL-5795 — the wallets list hides these by default).
 *
 * `mapApiWallet` (hooks/use-wallets.ts) works this out once already: the alias it feeds to
 * `walletDisplayName` is the gateway's display name first, then the stored alias, and `derived`
 * is true only when that value was machine-generated or missing — the exact "no human alias and
 * no human display name" case. `Wallet` keeps only the name and the stored alias, and it keeps
 * the alias *only* when the name had to be derived, so a set `storedAlias` carries that verdict
 * for the page. The `isMachineAlias` fallback catches a stored alias that is the wallet id
 * itself, and a name that is itself a hash or a timestamped lab alias.
 *
 * Known gap (mapper-level, not fixable from a page): a gateway row with no display name, alias,
 * name or label field at all collapses to an empty stored alias (`storedAlias: null`) even
 * though its name was derived, so such a wallet stays visible. Every live gateway row carries
 * `accountAlias` (the wallet id when the operator chose none), which this catches.
 */
export function isMachineNamedWallet(wallet: WalletNameSource): boolean {
  if ((wallet.storedAlias ?? "").trim()) return true;
  return isMachineAlias(wallet.name ?? "", wallet.walletId);
}

function titleCase(word: string): string {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

/** "IncomingFunds_1790686047753" -> "Incoming Funds"; "trio_5925…" -> "" (no readable stem). */
function readableStem(alias: string): string {
  const withoutStamp = alias.trim().replace(TIMESTAMP_SUFFIX, "");
  if (PREFIXED_HEX.test(withoutStamp) || HEX_TOKEN.test(withoutStamp)) return "";
  const words = withoutStamp
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[\s_-]+/)
    .filter((w) => w && !HEX_TOKEN.test(w));
  return words.map(titleCase).join(" ");
}

function shortAddress(address: string): string {
  const a = address.trim();
  return a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}

const TRIO_ALIAS = /^trio_[0-9a-f]{12,}$/i;

export function walletDisplayName(input: WalletNameInput): WalletDisplayName {
  const alias = (input.alias ?? "").trim();
  if (alias && !isMachineAlias(alias, input.walletId)) {
    return { name: alias, derived: false, alias };
  }

  const network = (input.network ?? "").trim();
  const chain = (input.chain ?? "").trim();
  const place = titleCase(network || chain);
  const stem = alias ? readableStem(alias) : "";
  const base = stem
    ? stem
    : TRIO_ALIAS.test(alias)
      ? `${place ? `${place} ` : ""}phone co-signed wallet`
      : `${place ? `${place} ` : ""}wallet`;
  const tail = input.address && input.address.trim() && input.address.trim() !== "—"
    ? shortAddress(input.address)
    : input.walletId
      ? input.walletId.trim().slice(-6)
      : "";
  const name = titleCase(tail ? `${base} · ${tail}` : base);
  return { name, derived: true, alias };
}
